/**
 * Client Tier Badge Component
 * Looks up tier config (color, display name) from the org-admin-managed
 * client_tiers table. Falls back to a neutral chip for unknown tier names
 * so legacy data keeps rendering.
 */

import React from 'react';
import { Chip } from '@mui/material';
import { StarBorder as StarBorderIcon } from '@mui/icons-material';
import { useClientTierConfigs } from '../hooks/useClientTiers';
import type { ClientTier } from '../types/client';

interface ClientTierBadgeProps {
  tier: ClientTier;
  size?: 'small' | 'medium';
}

// Pick a readable text color (black or white) for an arbitrary background hex.
const textColorFor = (hex: string): string => {
  const m = hex.replace('#', '');
  if (m.length !== 3 && m.length !== 6) return '#fff';
  const expand = (s: string) => (s.length === 3 ? s.split('').map((c) => c + c).join('') : s);
  const full = expand(m);
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luma > 0.6 ? '#000' : '#fff';
};

export const ClientTierBadge: React.FC<ClientTierBadgeProps> = ({ tier, size = 'small' }) => {
  const { data: configs } = useClientTierConfigs();

  const config = configs?.find(
    (c) => c.name.toUpperCase() === (tier || '').toUpperCase(),
  );

  const bg = config?.color ?? '#6B7280';
  const label = config?.name ?? (tier || 'Unassigned');
  const fg = textColorFor(bg);

  return (
    <Chip
      icon={<StarBorderIcon fontSize="small" />}
      label={label}
      size={size}
      sx={{
        backgroundColor: bg,
        color: fg,
        fontWeight: 600,
        '& .MuiChip-icon': { color: fg },
      }}
    />
  );
};

export default ClientTierBadge;
